import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { isConversionPath, shouldLoadContentsquare } from './useContentsquare';
import { afterPageLoad } from '../utils/afterPageLoad';

// Chave pública do projeto (vai no JS do site por natureza). Override por env no build.
const POSTHOG_KEY =
  process.env.REACT_APP_POSTHOG_KEY || 'phc_z6E6biU3ddmQyUpe5Zpb3TRbj2x4y6uQ8jQYoijYrVxH';
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';

/** Além do funil de conversão (LPs e /register*): login, ativação e conexão de plataformas. */
const EXTRA_PATH_PREFIXES = [
  '/acess',
  '/reset-password',
  '/create-project',
  '/invites',
  '/linked-accounts',
];
const PLATFORM_LOGIN_RE = /^\/[a-z-]+-login(\/|$)/;

export function isPostHogPath(pathname) {
  if (!pathname || typeof pathname !== 'string') return false;
  if (isConversionPath(pathname)) return true;
  const path = pathname.toLowerCase();
  if (PLATFORM_LOGIN_RE.test(path)) return true;
  return EXTRA_PATH_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

/** Rotas públicas/ativação ou etapas de onboarding dentro do projeto. */
export function shouldLoadPostHog({ pathname, onboardingStep } = {}) {
  return isPostHogPath(pathname) || shouldLoadContentsquare({ onboardingStep });
}

let phClient = null;
let pathAllowed = false;
let onboardingActive = false;
let loading = false;

function syncRecording() {
  if (!phClient) return;
  try {
    if (pathAllowed || onboardingActive) phClient.startSessionRecording();
    else phClient.stopSessionRecording();
  } catch {
    /* sem gravação não quebra a página */
  }
}

function loadPostHog() {
  if (typeof window === 'undefined' || !POSTHOG_KEY || phClient || loading) return;
  loading = true;

  // Espera o load da página: o SDK e o recorder não competem com o LCP das LPs.
  afterPageLoad(initPostHog);
}

function initPostHog() {
  import('posthog-js')
    .then(({ default: posthog }) => {
      posthog.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,
        person_profiles: 'identified_only',
        capture_pageview: 'history_change',
        enable_heatmaps: true,
        // Surveys não são usados: evita baixar surveys.js (~37 KB) em toda sessão.
        disable_surveys: true,
        session_recording: {
          maskAllInputs: true,
          maskTextSelector: '[data-ph-mask]',
        },
      });
      phClient = posthog;
      syncRecording();
    })
    .catch(() => {})
    .finally(() => {
      loading = false;
    });
}

function refresh() {
  if (pathAllowed || onboardingActive) loadPostHog();
  syncRecording();
}

/** Etapas de onboarding em tela cheia (pages/project.js). */
export function setPostHogOnboarding(active) {
  onboardingActive = Boolean(active);
  refresh();
}

/**
 * Grava só nas rotas de conversão/ativação e nas etapas de onboarding.
 * Fora delas a gravação é pausada, para não consumir cota com o painel do cliente.
 * Inputs são mascarados por padrão (chaves de API, cartão, senha).
 */
export function usePostHog() {
  const { pathname } = useLocation();

  useEffect(() => {
    pathAllowed = isPostHogPath(pathname);
    refresh();
  }, [pathname]);
}
